import { keyframes } from '@mui/system';
import { IGetJuryCampaigns, IJuryDuties } from '../../redux/jury/types';
import { JuryDutyFilterKey } from './JuryDutyObsidianList';

/** Unfiltered + pending buckets — API splits invite-pending duties from active assignments. */
export const JURY_DUTY_API_STATUSES: Array<IGetJuryCampaigns['status'] | undefined> = [undefined, 'pending'];

/** Invite or host approval still outstanding — not the same as “has entries left to score”. */
export function isJuryDutyPending(duty: IJuryDuties): boolean {
    if (duty.pending) return true;
    if (!duty.accepted_invite) return true;
    return false;
}

export function isJuryDutyJudged(duty: IJuryDuties): boolean {
    if (duty.completed) return true;
    const total = duty.totalCompleted + duty.totalRemaining;
    if (total > 0 && duty.totalRemaining === 0) return true;
    return duty.percentageScored >= 100;
}

/**
 * Avoid wiping a populated jury-duty list when a background refresh returns an empty snapshot.
 */
export function resolveJuryCampaignsForStore(existing: IJuryDuties[], incoming: IJuryDuties[]): IJuryDuties[] {
    if (incoming.length > 0) return incoming;
    if (existing.length > 0) return existing;
    return incoming;
}

export function mergeJuryDutiesById(duties: IJuryDuties[]): IJuryDuties[] {
    const byId = new Map<string, IJuryDuties>();
    for (const duty of duties) {
        if (duty?._id) {
            byId.set(duty._id, duty);
        }
    }
    return Array.from(byId.values());
}

export function filterJuryDutiesByTab(duties: IJuryDuties[], tab: JuryDutyFilterKey): IJuryDuties[] {
    switch (tab) {
        case 'pending':
            return duties.filter(isJuryDutyPending);
        case 'judged':
            return duties.filter(isJuryDutyJudged);
        case 'all':
        default:
            return duties;
    }
}

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const stripHtml = (html: string | undefined) =>
    (html ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();

export const phaseVisual = (phase: string | undefined) => {
    const p = (phase ?? 'none').toLowerCase();
    if (p === 'closed')
        return {
            label: 'Closed',
            bg: 'rgba(127, 29, 29, 0.2)',
            color: '#f87171',
            border: 'rgba(239, 68, 68, 0.2)',
            dot: '#ef4444',
            pulse: false,
        };
    if (p === 'judging' || p === 'vote')
        return {
            label: p === 'vote' ? 'Voting' : 'Judging',
            bg: 'rgba(20, 83, 45, 0.2)',
            color: '#4ade80',
            border: 'rgba(34, 197, 94, 0.2)',
            dot: '#22c55e',
            pulse: true,
        };
    if (p === 'submission' || p === 'registration')
        return {
            label: p === 'registration' ? 'Registration' : 'Open',
            bg: 'rgba(30, 58, 138, 0.2)',
            color: '#60a5fa',
            border: 'rgba(59, 130, 246, 0.2)',
            dot: '#3b82f6',
            pulse: false,
        };
    return {
        label: p.replace(/_/g, ' ') || 'Planning',
        bg: 'rgba(30, 58, 138, 0.2)',
        color: '#60a5fa',
        border: 'rgba(59, 130, 246, 0.2)',
        dot: '#3b82f6',
        pulse: false,
    };
};

export const campaignMetaBadge = (item: IJuryDuties) => {
    if (item.pending) return 'Pending';
    if (item.contest?.premium) return 'Premium';
    if (item.completed) return 'Complete';
    if (item.incomplete) return 'Active';
    return 'Ongoing';
};
